/**
 * Cross-tab activity tracking utility
 * Uses localStorage to share activity state across all tabs/windows
 */

const ACTIVITY_KEY = 'fortress_user_activity_timestamp';
const SESSION_EXTENDED_KEY = 'fortress_session_extended_timestamp';
const SESSION_LOGOUT_KEY = 'fortress_session_logout';
const SESSION_LOGIN_KEY = 'fortress_session_login';
const ACTIVITY_THRESHOLD = 1000; // 1 second threshold for considering activity "recent"

// Module-level timestamp of the most recent login on THIS tab.
// Used to ignore cross-tab logout broadcasts that predate this login.
let localLoginTimestamp: number = 0;

/**
 * Records that a fresh login occurred on this tab.
 * @param {number} [ts] - Optional login timestamp; defaults to Date.now().
 *   Pass the broadcast payload's timestamp when reacting to a cross-tab login
 *   event so the guard reflects the actual re-login time rather than receive time.
 */
export const recordLocalLoginTimestamp = (ts?: number) => {
  localLoginTimestamp = ts || Date.now();
};

/**
 * Returns the timestamp of the most recent login on this tab, or 0 if none.
 */
export const getLocalLoginTimestamp = (): number => localLoginTimestamp;

/**
 * Updates the activity timestamp in localStorage
 * This will trigger storage events in other tabs
 */
export const updateActivityTimestamp = () => {
  try {
    const timestamp = Date.now();
    localStorage.setItem(ACTIVITY_KEY, timestamp.toString());
    return timestamp;
  } catch (error) {
    // localStorage might not be available (private browsing, etc.)
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Failed to update activity timestamp:', error);
    }
    return null;
  }
};

/**
 * Gets the latest activity timestamp from localStorage
 */
export const getLatestActivityTimestamp = (): ?number => {
  try {
    const timestamp = localStorage.getItem(ACTIVITY_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Checks if there's been recent activity in any tab
 * @param {number} maxAge - Maximum age in milliseconds to consider activity "recent"
 * @returns {boolean} - True if there's been activity within maxAge
 */
export const hasRecentActivity = (
  maxAge: number = ACTIVITY_THRESHOLD,
): boolean => {
  const timestamp = getLatestActivityTimestamp();
  if (!timestamp) return false;
  const age = Date.now() - timestamp;
  return age < maxAge;
};

/**
 * Broadcasts a session extension event to other tabs
 * This will trigger storage events in other tabs so they can close their modals
 * @param {string} userId - The ID of the user extending the session, so other tabs
 *   can ignore the event if it belongs to a different logged-in user
 */
export const broadcastSessionExtended = (userId: string) => {
  try {
    const payload = JSON.stringify({ timestamp: Date.now(), userId });
    localStorage.setItem(SESSION_EXTENDED_KEY, payload);
    // Remove it immediately so it can be triggered again on next extension
    setTimeout(() => {
      try {
        localStorage.removeItem(SESSION_EXTENDED_KEY);
      } catch (e) {
        // Ignore cleanup errors
      }
    }, 100);
    return payload;
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Failed to broadcast session extension:', error);
    }
    return null;
  }
};

/**
 * Broadcasts a fresh login event to other tabs.
 * Background tabs that still have a stale idle-logout countdown running
 * will cancel it when they receive this event.
 * @param {string} userId - The ID of the user who just logged in
 */
export const broadcastLogin = (userId: string) => {
  try {
    const payload = JSON.stringify({ timestamp: Date.now(), userId });
    localStorage.setItem(SESSION_LOGIN_KEY, payload);
    setTimeout(() => {
      try {
        localStorage.removeItem(SESSION_LOGIN_KEY);
      } catch (e) {
        // Ignore cleanup errors
      }
    }, 100);
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Failed to broadcast login:', error);
    }
  }
};

/**
 * Broadcasts a logout event to other tabs
 * @param {string} userId - The ID of the user logging out
 */
export const broadcastLogout = (userId: string) => {
  try {
    const payload = JSON.stringify({ timestamp: Date.now(), userId });
    localStorage.setItem(SESSION_LOGOUT_KEY, payload);
    // Remove immediately — storage events only fire in OTHER tabs, so this
    // tab won't receive its own broadcast. The remove clears state for the
    // next logout cycle.
    setTimeout(() => {
      try {
        localStorage.removeItem(SESSION_LOGOUT_KEY);
      } catch (e) {
        // Ignore cleanup errors
      }
    }, 100);
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Failed to broadcast logout:', error);
    }
  }
};

/**
 * Sets up a listener for storage events from other tabs
 * When activity or session extension is detected in another tab, calls the appropriate callback
 * @param {Function} onActivityDetected - Callback when activity is detected in another tab
 * @param {Function} onSessionExtended - Callback when session is extended in another tab
 * @param {Function} onLogout - Callback when logout is broadcast from another tab
 * @param {Function} onLogin - Callback when a fresh login is broadcast from another tab
 * @returns {Function} - Cleanup function to remove the listener
 */
export const setupCrossTabActivityListener = (
  onActivityDetected: Function,
  onSessionExtended: ?Function,
  onLogout: ?Function,
  onLogin: ?Function,
): Function => {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === ACTIVITY_KEY && event.newValue) {
      const timestamp = parseInt(event.newValue, 10);
      if (!isNaN(timestamp)) {
        onActivityDetected(timestamp);
      }
    } else if (
      event.key === SESSION_EXTENDED_KEY &&
      event.newValue &&
      onSessionExtended
    ) {
      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed && parsed.userId) {
          onSessionExtended(parsed);
        }
      } catch (e) {
        // Ignore malformed payloads
      }
    } else if (event.key === SESSION_LOGOUT_KEY && event.newValue && onLogout) {
      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed && parsed.userId) {
          onLogout(parsed);
        }
      } catch (e) {
        // Ignore malformed payloads
      }
    } else if (event.key === SESSION_LOGIN_KEY && event.newValue && onLogin) {
      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed && parsed.userId) {
          onLogin(parsed);
        }
      } catch (e) {
        // Ignore malformed payloads
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  // Return cleanup function
  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
};
